import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_USER_DEFAULT } from '../../../app/constants/paths.constant';
import type { ReferencesContext_Civility } from '../../../contexts/ReferencesContext';

import type { UsersPage_List_User } from '.';

interface Props {
  readonly user: UsersPage_List_User;
}

const Action = ({ user }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_USER_DEFAULT, { id: user._id });
    void navigate(path);
  }, [navigate, user._id]);

  const actionLabel = "Consulter l'Utilisateur";

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  civilities: readonly ReferencesContext_Civility[],
): readonly GridColDef<UsersPage_List_User>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (params: Readonly<GridRowParams<UsersPage_List_User>>) => [
      <Action key="consult" user={params.row} />,
    ],
  },
  {
    field: nameof<UsersPage_List_User>('email'),
    headerName: 'Adresse e-mail',
    width: 400,
    description: 'Adresse e-mail',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: 'civility',
    headerName: 'Civilité',
    width: 150,
    description: 'Civilité',
    editable: false,
    sortable: true,
    type: 'singleSelect',
    valueGetter: (_: unknown, row: Readonly<UsersPage_List_User>) =>
      civilities.find((civility) => civility._id === row.person.civilityId)
        ?.code ?? 'Inconnu',
    valueOptions: civilities.map((civility) => ({
      value: civility.code,
      label: civility.label,
    })),
  },
  {
    field: 'firstName',
    headerName: 'Prénom',
    width: 300,
    description: 'Prénom',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (_: unknown, row: Readonly<UsersPage_List_User>) =>
      row.person.firstName,
  },
  {
    field: 'lastName',
    headerName: 'Nom',
    width: 300,
    description: 'Nom',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (_: unknown, row: Readonly<UsersPage_List_User>) =>
      row.person.lastName,
  },
  {
    field: 'organizations',
    headerName: 'Organisation(s)',
    width: 400,
    description: 'Organisation(s)',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (_: unknown, row: Readonly<UsersPage_List_User>) =>
      row.person.activeMemberships
        .map((activeMembership) => activeMembership.organization.name)
        .join(', '),
  },
  {
    field: 'lastSeenAt',
    headerName: 'Date de dernière connexion',
    width: 250,
    description: 'Date de dernière connexion',
    editable: false,
    sortable: true,
    type: 'dateTime',
    valueGetter: (_: unknown, row: Readonly<UsersPage_List_User>) =>
      row.lastSeenAt ? new Date(row.lastSeenAt) : null,
  },
];
